import { SuccessCallback } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { AxiosResponseHeaders } from 'axios';
import { ITransactionService } from '../entities/ITransactionService';
import { ConsolidatedTransactions } from '../entities/ConsolidatedTransaction/ConsolidatedTransactionResponseDTO';
import { TransactionStatus } from '../entities/TransactionStatus';
import { TransactionType } from '../entities/TransactionType';
import { CreatedFavouriteTransaction, FavouriteTransaction } from '../entities/FavouriteTransaction';
import { ITransactionRepository } from '../entities/ITransactionRepository';
import { AccountExtraitTransactions } from '../entities/AccountExtraitTransaction/AccountExtraitTransactionResponseDTO';
import { OrderStatus } from '../entities/OrderStatus';
import { TransactionDocument, TransactionDocumentData } from '../entities/TransactionDocuments';
import { AccountExtraitTransactionsPrint } from '../entities/AccountExtraitTransaction/AccountExtraitTransactionsPrintResponseDTO';
import { ConsolidatedTransactionsPrint } from '../entities/ConsolidatedTransaction/ConsolidatedTransactionsPrintResponseDTO';
import { ConsolidatedTransactionDetailsPrint } from '../entities/ConsolidatedTransaction/ConsolidatedTransactionDetailsPrintResponseDTO';
import { TransactionDocumentDeleteData } from '../entities/TransactionDocuments/TransactionDocumentDeleteResponseDTO';
import { TransactionsWithDealerRateServiceBody } from '../entities/TransactionWithDealerRate/TransactionWithDealerRateResponseDTO';

export class TransactionServiceImpl implements ITransactionService {
  itemRepo: ITransactionRepository;

  constructor(ir: ITransactionRepository) {
    this.itemRepo = ir;
    this.GetAccountExtraitTransactions = this.GetAccountExtraitTransactions.bind(this);
    this.GetConsolidatedTransactions = this.GetConsolidatedTransactions.bind(this);
    this.GetFavouriteTransactions = this.GetFavouriteTransactions.bind(this);
    this.CreateNewFavouriteTransaction = this.CreateNewFavouriteTransaction.bind(this);
    this.EditFavouriteTransaction = this.EditFavouriteTransaction.bind(this);
    this.DeleteFavouriteTransaction = this.DeleteFavouriteTransaction.bind(this);
    this.GetTransactionStatuses = this.GetTransactionStatuses.bind(this);
    this.GetTransactionTypes = this.GetTransactionTypes.bind(this);
    this.GetOrderStatuses = this.GetOrderStatuses.bind(this);
    this.GetConsolidatedTransactionDetails = this.GetConsolidatedTransactionDetails.bind(this);
    this.GetTransactionDocuments = this.GetTransactionDocuments.bind(this);
    this.GetTransactionDocumentData = this.GetTransactionDocumentData.bind(this);
    this.GetAccountTransactionsPrint = this.GetAccountTransactionsPrint.bind(this);
    this.GetConsolidatedTransactionsPrint = this.GetConsolidatedTransactionsPrint.bind(this);
    this.GetConsolidatedTransactionDetailsPrint = this.GetConsolidatedTransactionDetailsPrint.bind(this);
    this.GetSwiftConfirmationPrint = this.GetSwiftConfirmationPrint.bind(this);
    this.UploadDocument = this.UploadDocument.bind(this);
    this.DeleteDocument = this.DeleteDocument.bind(this);
    this.GetTransactionsWithDealerRate = this.GetTransactionsWithDealerRate.bind(this);
  }

  GetAccountExtraitTransactions(params: {
    accountNumber: string;
    pageSize: number;
    pageOffset: number;
    allEntries?: string;
    dateFrom?: string;
    dateTo?: string;
    fromAmount?: number;
    toAmount?: number;
    transactionType?: number;
  }): Promise<AccountExtraitTransactions> {
    return this.itemRepo.GetAccountExtraitTransactions(
      params.accountNumber,
      params.pageSize,
      params.pageOffset,
      params?.allEntries,
      params?.dateFrom,
      params?.dateTo,
      params?.fromAmount,
      params?.toAmount,
      params?.transactionType,
    );
  }

  GetConsolidatedTransactionDetails(params: {
    orderId: number;
    accountNumber: string;
    successCallback?: SuccessCallback;
  }) {
    return this.itemRepo.GetConsolidatedTransactionDetails(
      params.orderId,
      params.accountNumber,
      params?.successCallback,
    );
  }

  GetConsolidatedTransactions(params: {
    beneficiaryName: string;
    fromAmount: number;
    toAmount: number;
    fromTrxDate: string;
    toTrxDate: string;
    pageSize: number;
    pageOffset: number;
    customerId: number;
    valueDateFrom?: string;
    valueDateTo?: string;
    orderBeneficiaryAccount?: string;
    orderStatus?: number;
    accountNumber?: string;
    orderDirection?: number;
  }): Promise<ConsolidatedTransactions> {
    return this.itemRepo.GetConsolidatedTransactions(
      params.beneficiaryName,
      params.fromAmount,
      params.toAmount,
      params.fromTrxDate,
      params.toTrxDate,
      params.pageSize,
      params.pageOffset,
      params.customerId,
      params.valueDateFrom,
      params.valueDateTo,
      params?.orderBeneficiaryAccount,
      params?.orderStatus,
      params?.accountNumber,
      params?.orderDirection,
    );
  }

  GetFavouriteTransactions(): Promise<FavouriteTransaction[]> {
    return this.itemRepo.GetFavouriteTransactions();
  }

  CreateNewFavouriteTransaction(params: {
    appTransactionId: number;
    requestData: string;
    description: string;
    transactionStatus: number;
    enabled: boolean;
  }): Promise<CreatedFavouriteTransaction> {
    return this.itemRepo.CreateNewFavouriteTransaction(
      params.appTransactionId,
      params.requestData,
      params.description,
      params.transactionStatus,
      params.enabled,
    );
  }

  EditFavouriteTransaction(params: {
    data: FavouriteTransaction;
    successCallback: SuccessCallback;
  }): Promise<AxiosResponseHeaders> {
    return this.itemRepo.EditFavouriteTransaction(params.data, params.successCallback);
  }

  DeleteFavouriteTransaction(params: {
    appTransactionUniqueId: number;
    successCallback: SuccessCallback;
  }): Promise<AxiosResponseHeaders> {
    return this.itemRepo.DeleteFavouriteTransaction(params.appTransactionUniqueId, params.successCallback);
  }

  GetTransactionStatuses(): Promise<TransactionStatus[]> {
    return this.itemRepo.GetTransactionStatuses();
  }

  GetTransactionTypes(): Promise<TransactionType[]> {
    return this.itemRepo.GetTransactionTypes();
  }

  GetOrderStatuses(): Promise<OrderStatus[]> {
    return this.itemRepo.GetOrderStatuses();
  }

  GetTransactionDocuments(params: { customerId: number; orderId: number | string }): Promise<TransactionDocument[]> {
    return this.itemRepo.GetTransactionDocuments(params.customerId, params.orderId);
  }

  GetTransactionDocumentData(params: {
    customerId: number;
    fileSn: number;
    referenceNumber: string;
  }): Promise<TransactionDocumentData> {
    return this.itemRepo.GetTransactionDocumentData(params.customerId, params.fileSn, params.referenceNumber);
  }

  GetConsolidatedTransactionDetailsPrint(params: {
    orderId: number;
    accountNumber: string;
  }): Promise<ConsolidatedTransactionDetailsPrint> {
    return this.itemRepo.GetConsolidatedTransactionDetailsPrint(params.orderId, params.accountNumber);
  }

  GetSwiftConfirmationPrint(params: {
    orderId: number;
    accountNumber: string;
  }): Promise<ConsolidatedTransactionDetailsPrint> {
    return this.itemRepo.GetSwiftConfirmationPrint(params.orderId, params.accountNumber);
  }

  GetAccountTransactionsPrint(params: {
    accountNumber: string;
    type: string;
    allEntries?: string | undefined;
    dateFrom?: string | undefined;
    dateTo?: string | undefined;
    fromAmount?: number | undefined;
    toAmount?: number | undefined;
    transactionType?: number | undefined;
  }): Promise<AccountExtraitTransactionsPrint> {
    return this.itemRepo.GetAccountTransactionsPrint(
      params.accountNumber,
      params.type,
      params?.allEntries,
      params?.dateFrom,
      params?.dateTo,
      params?.fromAmount,
      params?.toAmount,
      params?.transactionType,
    );
  }

  GetConsolidatedTransactionsPrint(params: {
    type: string;
    fromAmount: number;
    toAmount: number;
    fromTrxDate: string;
    toTrxDate: string;
    customerId: number;
    valueDateFrom?: string | undefined;
    valueDateTo?: string | undefined;
    beneficiaryName?: string | undefined;
    orderStatus?: number | undefined;
    accountNumber?: string | undefined;
    orderDirection?: number | undefined;
  }): Promise<ConsolidatedTransactionsPrint> {
    return this.itemRepo.GetConsolidatedTransactionsPrint(
      params.type,
      params.fromAmount,
      params.toAmount,
      params.fromTrxDate,
      params.toTrxDate,
      params.customerId,
      params.valueDateFrom,
      params.valueDateTo,
      params.beneficiaryName,
      params.orderStatus,
      params.accountNumber,
      params.orderDirection,
    );
  }

  UploadDocument(params: {
    customerId: number;
    fileData: string;
    fileName: string;
    accountNumber: string;
    orderCode: number;
    successCallback: SuccessCallback;
  }): Promise<TransactionDocumentData> {
    return this.itemRepo.UploadDocument(
      params.customerId,
      params.fileData,
      params.fileName,
      params.accountNumber,
      params.orderCode,
      params.successCallback,
    );
  }

  DeleteDocument(params: {
    customerId: number;
    fileSn: number;
    referenceNumber: string;
    successCallback: SuccessCallback;
  }): Promise<TransactionDocumentDeleteData> {
    return this.itemRepo.DeleteDocument(
      params.customerId,
      params.fileSn,
      params.referenceNumber,
      params.successCallback,
    );
  }

  GetTransactionsWithDealerRate(params: {
    customerId: string;
    fromOrderAmountFilter: number;
    toOrderAmountFilter: number;
    fromTrxDate: string;
    toTrxDate: string;
    successCallback?: SuccessCallback;
  }): Promise<TransactionsWithDealerRateServiceBody> {
    return this.itemRepo.GetTransactionsWithDealerRate(
      params.customerId,
      params.fromOrderAmountFilter,
      params.toOrderAmountFilter,
      params.fromTrxDate,
      params.toTrxDate,
      params?.successCallback,
    );
  }
}
